// Pure decision helpers for multi-year navigation on the competitor-set
// detail page (docs/specs/2026-07-28-comp-set-multi-year-design.md).
//
// Week math uses UTC so generated weekStarts match the backend's
// generateWeekBoundaries (API containers run UTC). Do NOT reuse
// getCharterWeeks() from comp-set-shared here: it builds local-time Dates
// and serializes with toISOString(), which shifts every weekStart back one
// day for positive-UTC-offset browsers.

/** Saturday-start charter weeks of a year, as YYYY-MM-DD weekStart strings. */
function charterWeekStarts(year: number): string[] {
  const jan1 = new Date(Date.UTC(year, 0, 1));
  const daysUntilSat = (6 - jan1.getUTCDay() + 7) % 7;
  const current = new Date(Date.UTC(year, 0, 1 + daysUntilSat));
  const endOfYear = Date.UTC(year, 11, 31);

  const starts: string[] = [];
  while (current.getTime() <= endOfYear) {
    starts.push(current.toISOString().split('T')[0]!);
    current.setUTCDate(current.getUTCDate() + 7);
  }
  return starts;
}

/** Years offered by the year selector: current and next. */
export function getYearOptions(now: Date): number[] {
  const current = now.getUTCFullYear();
  return [current, current + 1];
}

/**
 * Where the year selector lands: the current/upcoming week when switching
 * to the current year (same default as first page load), else the target
 * year's first week.
 */
export function getYearSwitchTarget(targetYear: number, now: Date): string {
  const weeks = charterWeekStarts(targetYear);
  if (targetYear === now.getUTCFullYear()) {
    const today = now.toISOString().split('T')[0]!;
    const upcoming = weeks.find((w) => w >= today);
    if (upcoming) return upcoming;
  }
  return weeks[0]!;
}

/**
 * Cross-year chevron rollover. Returns the weekStart to navigate to when a
 * chevron is pressed at a calendar boundary, or null when rollover is not
 * allowed there (which is also the chevron's disabled signal at the
 * boundary). Next rolls forward while year+1 <= currentYear+1; prev rolls
 * back while year-1 >= currentYear.
 */
export function getRolloverTarget(
  direction: 'prev' | 'next',
  year: number,
  index: number,
  calendarLength: number,
  now: Date
): string | null {
  const currentYear = now.getUTCFullYear();
  if (direction === 'next') {
    if (index !== calendarLength - 1) return null;
    if (year + 1 > currentYear + 1) return null;
    return charterWeekStarts(year + 1)[0]!;
  }
  if (index !== 0) return null;
  if (year - 1 < currentYear) return null;
  const weeks = charterWeekStarts(year - 1);
  return weeks[weeks.length - 1]!;
}
